import React, { useId, useState } from 'react';
import { Modal } from 'react-bootstrap';
import PropTypes from 'prop-types';

function UploadMediaModal({ show, onClose, onUpload, isUploading, uploadProgress, isExternalDragging = false }) {
    const inputId = useId();
    const [isDragging, setIsDragging] = useState(false);
    const isDropActive = isDragging || isExternalDragging;

    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
        if (isUploading) {
            return;
        }
        const file = event.dataTransfer.files?.[0];
        if (file) {
            onUpload(file);
        }
    };

    const handleFileSelect = (event) => {
        const file = event.target.files?.[0];
        if (file) {
            onUpload(file);
        }
        event.target.value = '';
    };

    return (
        <Modal
            show={show}
            onHide={onClose}
            centered
            backdrop="static"
            keyboard={false}
            dialogClassName="my-files-upload-modal"
            contentClassName="my-files-upload-modal__content"
        >
            <Modal.Header className="my-files-upload-modal__header border-0 d-flex align-items-center">
                <div className="d-flex align-items-center gap-3 min-width-0 flex-grow-1">
                    <span className="my-files-upload-modal__icon-badge flex-shrink-0">
                        <i className="bi bi-cloud-arrow-up"></i>
                    </span>
                    <div className="min-width-0">
                        <Modal.Title className="h5 mb-0 fw-semibold">Upload Media</Modal.Title>
                        <div className="small text-muted">Drag a file here or browse from your device</div>
                    </div>
                </div>
                <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary rounded-pill ecx-modal__close-btn ms-auto flex-shrink-0"
                    onClick={onClose}
                    aria-label="Close"
                    disabled={isUploading}
                >
                    <i className="bi bi-x-lg"></i>
                </button>
            </Modal.Header>

            <Modal.Body className="my-files-upload-modal__body">
                <div
                    className={`my-files-upload p-5 text-center${isDropActive ? ' is-dragging' : ''}${
                        isUploading ? ' is-uploading' : ''
                    }`}
                    onClick={() => !isUploading && document.getElementById(inputId)?.click()}
                    onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!isUploading) {
                            setIsDragging(true);
                        }
                    }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={handleDrop}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (!isUploading && (e.key === 'Enter' || e.key === ' ')) {
                            document.getElementById(inputId)?.click();
                        }
                    }}
                >
                    <span className="my-files-upload__zone-badge">
                        <i className="bi bi-arrows-move"></i>
                        Drop zone
                    </span>

                    <input
                        type="file"
                        className="d-none"
                        id={inputId}
                        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/avif,image/svg+xml,image/bmp,image/tiff,application/pdf"
                        onChange={handleFileSelect}
                        disabled={isUploading}
                    />

                    <span className="my-files-upload__icon">
                        <i className="bi bi-cloud-arrow-up"></i>
                    </span>
                    <h6 className="mb-1 fw-semibold text-dark">
                        {isDropActive ? 'Drop file here' : 'Drag & drop or click to upload'}
                    </h6>
                    <p className="mb-0 small text-muted">Supported formats: JPG, PNG, GIF, WebP, AVIF, SVG, PDF, and more</p>

                    {isUploading && (
                        <div className="mt-4 mx-auto" style={{ maxWidth: '320px' }}>
                            <div className="progress" style={{ height: '6px' }}>
                                <div
                                    className="progress-bar bg-primary progress-bar-striped progress-bar-animated"
                                    role="progressbar"
                                    style={{ width: `${uploadProgress}%` }}
                                    aria-valuenow={uploadProgress}
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                ></div>
                            </div>
                            <small className="text-muted d-block mt-2">Uploading: {uploadProgress}%</small>
                        </div>
                    )}
                </div>
            </Modal.Body>

            <Modal.Footer className="my-files-upload-modal__footer border-0">
                <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary rounded-pill ecx-modal__footer-close-btn"
                    onClick={onClose}
                    disabled={isUploading}
                >
                    <i className="bi bi-x-circle me-1"></i>
                    Close
                </button>
            </Modal.Footer>
        </Modal>
    );
}

UploadMediaModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onUpload: PropTypes.func.isRequired,
    isUploading: PropTypes.bool.isRequired,
    uploadProgress: PropTypes.number.isRequired,
    isExternalDragging: PropTypes.bool,
};

export default UploadMediaModal;
